import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Tray, X, Users, Envelope, WarningCircle, CheckCircle } from "@phosphor-icons/react";
import { apiFetch } from "../api/auth-fetch";
import InboxTaskResult from './InboxTaskResult';
import {inboxPresentation, type InboxItem as Item} from './inboxPresentation';
const destination = (item: Item) => inboxPresentation(item).href;
const itemTitle = (item: Item) => inboxPresentation(item).title;
export default function Inbox({compact=false}:{compact?:boolean}) {
  const [open, setOpen] = useState(false),
    [items, setItems] = useState<Item[]>([]),
    [filter, setFilter] = useState("all"),
    [error, setError] = useState(""),
    [loaded, setLoaded] = useState(false),
    [busy, setBusy] = useState<Set<string>>(new Set());
  const busyRef = useRef(new Set<string>());
  const [itemErrors,setItemErrors]=useState<Record<string,string>>({});
  const [detailId, setDetailId] = useState<string | null>(null);
  const [detail, setDetail] = useState<Item | null>(null);
  const [detailError, setDetailError] = useState("");
  const detailSequence = useRef(0);
  const detailBack = useRef<HTMLButtonElement>(null);
  const detailTrigger = useRef<HTMLElement | null>(null);
  // INBOX-OPEN-03, INBOX-OPEN-06, INBOX-OPEN-07: opening is read-only and revalidates state.
  const loadDetail = async (id: string) => {
    const sequence = ++detailSequence.current;
    setDetailId(id); setDetail(null); setDetailError("");
    try {
      const response = await apiFetch(`/api/inbox?item=${encodeURIComponent(id)}`);
      const data = await response.json();
      if (sequence !== detailSequence.current) return;
      if (!response.ok) throw Error(data.error?.message || "Could not open this message. Try again.");
      setDetail(data.item);
    } catch (error) {
      if (sequence === detailSequence.current) setDetailError((error as Error).message);
    }
  };
  useEffect(()=>{
    if(!open||!detailId)return;
    const refresh=()=>{void loadDetail(detailId);};
    window.addEventListener('focus',refresh);
    return()=>window.removeEventListener('focus',refresh);
  },[open,detailId]);
  const backToList = () => {
    detailSequence.current++; setDetailId(null); setDetail(null); setDetailError("");

  };
  // INBOX-OPEN-08: restore focus during commit, without a frame in which Escape is lost.
  useLayoutEffect(() => { if (detailId) detailBack.current?.focus(); else if (open) detailTrigger.current?.focus(); }, [detailId]);
  const [pending, setPending] = useState(0);
  const [unread, setUnread] = useState(false);
  const filterRef = useRef(filter);
  filterRef.current = filter;
  const requestSequence = useRef(0);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const panel = useRef<HTMLElement>(null),
    openRef = useRef(false),
    seen = useRef(new Set<string>());
  openRef.current = open;
  const close = useRef<HTMLButtonElement>(null),
    trigger = useRef<HTMLButtonElement>(null),
    wasOpen = useRef(false);
  useEffect(()=>{
    if(!open)return;
    const escape=(event:KeyboardEvent)=>{if(event.key==='Escape'){event.preventDefault();event.stopPropagation();if(detailId)backToList();else setOpen(false);}};
    document.addEventListener('keydown',escape,true);
    return()=>document.removeEventListener('keydown',escape,true);
  },[open,detailId]);
  useLayoutEffect(()=>{if(open&&panel.current&&!panel.current.contains(document.activeElement))close.current?.focus();},[items,loaded]);
  const load = async (cursor?: string) => {
    const sequence = ++requestSequence.current;
    const query = new URLSearchParams({ filter: filterRef.current });
    if (cursor) query.set("cursor", cursor);
    const r = await apiFetch("/api/inbox?" + query);
    if (!r.ok) throw Error("Could not load Inbox. Try again.");
    const data = await r.json();
    if (sequence !== requestSequence.current) return;
    setPending(data.pending_count);
    setUnread(data.unread);
    setError("");
    setItems((prev) =>
      cursor
        ? [...new Map([...prev, ...data.items].map((i) => [i.id, i])).values()]
        : data.items,
    );
    setNextCursor(data.next_cursor);
    setLoaded(true);
  };
  useEffect(() => {
    let live = true;
    const refresh = () => {
      if (live && !openRef.current)
        void load().catch((e) => {
          if (live) setError(e.message);
        });
    };
    refresh();
    const t = setInterval(refresh, 15_000);
    return () => {
      live = false;
      clearInterval(t);
    };
  }, []);
  useEffect(() => {
    if (open) {
      close.current?.focus();
      void load().catch((e) => setError(e.message));
    } else if (wasOpen.current) {
      detailSequence.current++; setDetailId(null); setDetail(null); setDetailError("");
      trigger.current?.focus();
      setItems((prev) =>
        prev.map((i) => (seen.current.has(i.id) ? { ...i, read: true } : i)),
      );
    }
    wasOpen.current = open;
  }, [open]);
  useEffect(() => {
    if (!openRef.current) return;
    setLoaded(false);
    void load().catch((e) => setError(e.message));
  }, [filter]);
  useEffect(() => {
    if (
      !open ||
      !loaded ||
      !panel.current ||
      typeof IntersectionObserver === "undefined"
    )
      return;
    const observer = new IntersectionObserver(
      (entries) => {
        const ids = entries
          .filter((e) => e.isIntersecting)
          .map((e) => (e.target as HTMLElement).dataset.inboxId!)
          .filter((id) => id && !seen.current.has(id));
        if (!ids.length) return;
        ids.forEach((id) => seen.current.add(id));
        void apiFetch("/api/inbox", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ids, action: "read" }),
        })
          .then((r) => {
            if (!r.ok) throw Error();
          })
          .catch(() => ids.forEach((id) => seen.current.delete(id)));
      },
      { root: panel.current, threshold: 0.25 },
    );
    panel.current
      .querySelectorAll("[data-inbox-id]")
      .forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [open, loaded, items, filter]);
  // INBOX-OPEN-04, INBOX-OPEN-09, INBOX-OPEN-10: mutation state is separate from navigation.
  const act = async (item: Item, action: string) => {
    if (busyRef.current.has(item.id)) return;
    busyRef.current.add(item.id);setBusy(new Set(busyRef.current));
    setItemErrors(prev=>({...prev,[item.id]:""}));
    try {
      if (action === "accept" || action === "decline") {
        const r = await apiFetch("/api/workspace-invitations/accept", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ invitation_id: item.id.slice(7), action }),
        });
        const data = await r.json();
        if (!r.ok)
          throw Error(
            data.error?.message || "Invitation could not be processed.",
          );
        if (action === "accept") {
          window.location.assign(
            `/w/${encodeURIComponent(data.workspace_id)}`,
          );
          return;
        }
      } else {
        const r = await apiFetch("/api/inbox", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ids: [item.id], action }),
        });
        if (!r.ok) throw Error("Could not update this item.");
      }
      if (action === "decline") backToList();
      await load();
    } catch (e) {
      setItemErrors(prev=>({...prev,[item.id]:(e as Error).message}));
    } finally {
      busyRef.current.delete(item.id);setBusy(new Set(busyRef.current));
    }
  };
  const visible = items.filter((i) =>
    filter === "action" ? i.needs_action : filter === "unread" ? !i.read : true,
  );
  return (
    <>
      <button
        ref={trigger}
        className="workspace-toolbar-action pp-inbox-trigger"
        onClick={() => setOpen(true)}
        title="Inbox"
        aria-label={`Inbox${pending ? `, ${pending} pending` : ""}`}
      >
        <Tray size={18} />
        {!compact && <span>Inbox</span>}
        {pending > 0 ? (
          <span>{pending}</span>
        ) : unread ? (
          <span aria-label="Unread updates">·</span>
        ) : null}
      </button>
      {open &&
        createPortal(
          <div
            className="coastal-member-dialog"
            role="dialog"
            aria-modal="true"
            aria-label="Inbox"
            onClick={(e) => {
              if (e.target === e.currentTarget) setOpen(false);
            }}
            onKeyDown={(e) => {
              if (e.key === "Escape") { if (detailId) backToList(); else setOpen(false); }
              if (e.key === "Tab") {
                const focus = Array.from(
                  e.currentTarget.querySelectorAll<HTMLElement>(
                    "button:not(:disabled),a[href],summary",
                  ),
                ).filter(
                  (el) =>
                    el.getClientRects().length > 0 &&
                    (!el.closest("details:not([open])") ||
                      el.tagName === "SUMMARY"),
                );
                const a = focus[0],
                  b = focus.at(-1);
                if (e.shiftKey && document.activeElement === a) {
                  e.preventDefault();
                  b?.focus();
                } else if (!e.shiftKey && document.activeElement === b) {
                  e.preventDefault();
                  a?.focus();
                }
              }
            }}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 1600,
              display: "flex",
              justifyContent: "flex-end",
              background: "var(--ds-overlay)",
            }}
          >
            <section
              ref={panel}
              className="pp-inbox"
              style={{
                width: "min(100%,560px)",
                height: "100%",
                overflow: "auto",
                background: "var(--bg-primary)",
                color: "var(--text-primary)",
                padding: "32px clamp(16px,4vw,40px)",
                boxSizing: "border-box",
              }}
            >
              <header
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <h1 style={{ fontSize: 32, fontWeight: 500 }}>Inbox</h1>
                <button
                  ref={close}
                  aria-label="Close Inbox"
                  onClick={() => setOpen(false)}
                >
                  <X size={22} />
                </button>
              </header>
              {detailId && (
                <div aria-label="Invitation details">
                  <button ref={detailBack} onClick={backToList}>← Back to Inbox</button>
                  {!detail && !detailError && <p role="status">Opening invitation…</p>}
                  {detailError && <p role="alert">{detailError} <button onClick={() => void loadDetail(detailId)}>Retry</button></p>}
                  {/* INBOX-OPEN-05: mailbox proof controls acceptance, never title opening. */}
                  {detail && <>
                    <h2>{detail.can_accept ? `Invitation to ${detail.title}` : "Workspace invitation"}</h2>
                    {detail.can_accept ? <>
                      <p>You have been invited to create and share presentations in this workspace.</p>
                      <button className="pp-inbox-primary" disabled={busy.has(detail.id)} onClick={() => void act(detail, "accept")}>Join workspace</button>
                      <button disabled={busy.has(detail.id)} onClick={() => void act(detail, "decline")}>Decline</button>
                    </> : <>
                      <p>Verify your email address using the invitation link sent to your inbox. Then return here to accept the invitation.</p>
                      <button onClick={() => void loadDetail(detailId)}>Refresh verification status</button>
                    </>}
                  </>}
                  {detailId && itemErrors[detailId] && <p role="alert">{itemErrors[detailId]}</p>}
                </div>
              )}
              <div hidden={!!detailId}>
              <details>
                <summary
                  aria-label="Inbox actions"
                  style={{
                    cursor: "pointer",
                    color: "var(--text-secondary)",
                    fontSize: 13,
                  }}
                >
                  Actions
                </summary>
                <button
                  onClick={async () => {
                    try {
                      const r = await apiFetch("/api/inbox", {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ action: "read_all" }),
                      });
                      if (!r.ok) throw Error("Could not mark Inbox as read.");
                      await load();
                    } catch (e) {
                      setError((e as Error).message);
                    }
                  }}
                >
                  Mark all read
                </button>
              </details>
              <nav
                aria-label="Inbox filters"
                style={{ display: "flex", gap: 16, margin: "16px 0 32px" }}
              >
                {[
                  ["all", "All"],
                  ["action", "Needs action"],
                  ["unread", "Unread"],
                ].map(([id, label]) => (
                  <button
                    key={id}
                    aria-pressed={filter === id}
                    onClick={() => setFilter(id)}
                    style={{
                      border: "none",
                      background: "none",
                      color:
                        filter === id
                          ? "var(--text-primary)"
                          : "var(--text-secondary)",
                      font: "inherit",
                    }}
                  >
                    {label}
                  </button>
                ))}
              </nav>
              {error && (
                <p role="alert">
                  {error}{" "}
                  <button
                    onClick={() =>
                      void load()
                        .then(() => setError(""))
                        .catch((e) => setError(e.message))
                    }
                  >
                    Retry
                  </button>
                </p>
              )}
              {!loaded ? (
                <p role="status">Loading Inbox…</p>
              ) : visible.length === 0 ? (
                <p>You’re all caught up.</p>
              ) : (
                visible.map((i) => {
                  const view=inboxPresentation(i);
                  return <article data-inbox-key={i.id} data-inbox-id={!i.read?i.id:undefined} data-interaction={view.type} key={i.id} className="pp-inbox-card" aria-busy={busy.has(i.id)}>
                    <div className="pp-inbox-card-icon" aria-hidden="true">{view.type==='decision'?<Users size={19}/>:view.type==='prerequisite'?<Envelope size={19}/>:view.type==='problem'?<WarningCircle size={19}/>:i.kind==='task.completed'?<CheckCircle size={19}/>:<Tray size={19}/>}</div>
                    <div className="pp-inbox-card-body">
                      <small>{view.context}</small>
                      <h2>
                        {view.invitation&&!view.resolved?<button className="pp-inbox-title" onClick={event=>{detailTrigger.current=event.currentTarget;void loadDetail(i.id);}}>{view.title}</button>:view.href?<a className="pp-inbox-title" href={view.href} onClick={()=>void act(i,'read')}>{view.title}</a>:<span>{view.title}</span>}
                        {!i.read&&<span className="pp-inbox-unread" aria-label="Unread"> ·</span>}
                      </h2>
                      {view.description&&<p className="pp-inbox-description">{view.description}</p>}
                      {i.kind.startsWith('task.')&&i.deck_id&&i.subject_id&&<InboxTaskResult key={i.id} deckId={i.deck_id} turnId={i.subject_id} failed={i.kind==='task.failed'}/>}
                      {view.status&&<p className="pp-inbox-resolution" role="status">{view.status}</p>}
                      <div className="pp-inbox-card-actions">
                        {view.type==='decision'&&<><button className="pp-inbox-primary" disabled={busy.has(i.id)} onClick={()=>void act(i,'accept')}>Join workspace</button><button disabled={busy.has(i.id)} onClick={()=>void act(i,'decline')}>Decline</button></>}
                        {view.type==='prerequisite'&&<button className="pp-inbox-primary" onClick={event=>{detailTrigger.current=event.currentTarget;void loadDetail(i.id);}}>Verify email</button>}
                        {!view.invitation&&<button className="pp-inbox-secondary" disabled={busy.has(i.id)} onClick={()=>void act(i,'archive')}>Archive</button>}
                        {!i.read&&<button className="pp-inbox-secondary" disabled={busy.has(i.id)} onClick={()=>void act(i,'read')}>Mark read</button>}
                      </div>
                      {itemErrors[i.id]&&<p role="alert">{itemErrors[i.id]}</p>}
                    </div>
                  </article>;
                })
              )}

              {nextCursor && (
                <button
                  onClick={() =>
                    void load(nextCursor).catch((e) => setError(e.message))
                  }
                >
                  Load earlier messages
                </button>
              )}
              </div>
            </section>
          </div>,
          document.body,
        )}
    </>
  );
}
